import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useRef } from 'react';
import type { StoreApi } from 'zustand';
import { createStore, useStore } from 'zustand';

export type UiStateStore = {
    readonly actions: {
        readonly setLevelCreationTargetRoleId: (roleId: string | null) => void;
    };
    readonly levelCreationTargetRoleId: string | null;
};

export const UiStateContext = createContext<StoreApi<UiStateStore> | null>(null);

const createUiStateStore = (): StoreApi<UiStateStore> =>
    createStore<UiStateStore>((setState) => ({
        actions: {
            setLevelCreationTargetRoleId: (roleId: string | null) => {
                setState({ levelCreationTargetRoleId: roleId });
            },
        },
        levelCreationTargetRoleId: null,
    }));

export const UiStateProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const storeRef = useRef<StoreApi<UiStateStore>>(null);

    storeRef.current ??= createUiStateStore();

    return <UiStateContext.Provider value={storeRef.current}>{children}</UiStateContext.Provider>;
};

export const useUiState = (): UiStateStore => {
    const store = useContext(UiStateContext);

    if (store === null) {
        throw new Error('useUiState must be used within a UiStateProvider');
    }

    return useStore(store);
};
